import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import Avatar from '@/components/Avatar';
import DeleteConfirmModal from '@/components/DeleteConfirmModal';
import { useAuth } from '@/contexts/auth-context';
import { createComment, deleteComment, fetchComments, updateComment } from '@/apiEndpoints';
import { btnAccent, btnGhost, btnPrimary, card, surface } from '@/components/ui/design';

const MAX_LENGTH = 1000;

const sectionHeading = 'text-[28px] sm:text-[36px] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const textareaClass = 'w-full resize-y rounded-2xl bg-grey99 px-4 py-3 text-[15px] text-grey10 ring-1 ring-black/[0.04] placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60';
const smallBtn = 'h-8 rounded-full bg-grey99 px-3 text-[13px] font-semibold text-grey10 transition-colors hover:bg-grey95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:opacity-60 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10';

function errorMessage(error: any, fallback: string) {
    const message = error?.response?.data?.message;
    return Array.isArray(message) ? message.join(', ') : message || fallback;
}

export default function EventCommentsSection({ eventId, commentsDisabled }: { eventId?: string; commentsDisabled?: boolean }) {
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const currentUserId = user?.userId ? String(user.userId) : '';

    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');
    const [notice, setNotice] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [editError, setEditError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

    const queryKey = ['comments', eventId, currentUserId];
    const { data, isLoading, isError, refetch } = useQuery<any>({
        queryKey,
        queryFn: () => fetchComments(eventId as string, 1, 50),
        enabled: !!eventId && !commentsDisabled,
    });
    const comments: any[] = data?.data || [];
    const isOwn = (c: any) => !!currentUserId && String(c.userId) === currentUserId;
    const publicCount = comments.filter(c => !(isOwn(c) && c.status === 'pending')).length;

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['comments', eventId] });

    const postMutation = useMutation({
        mutationFn: (text: string) => createComment(eventId as string, text),
        onSuccess: () => {
            setDraft('');
            setDraftError('');
            setNotice('Your comment has been submitted and is awaiting approval.');
            refresh();
        },
        onError: (error: any) => {
            setNotice('');
            setDraftError(errorMessage(error, 'Your comment could not be sent. Please try again.'));
        },
    });

    const editMutation = useMutation({
        mutationFn: ({ id, text }: { id: string; text: string }) => updateComment(id, text),
        onSuccess: () => {
            setEditingId(null);
            setEditText('');
            setEditError('');
            setNotice('Your updated comment is awaiting approval.');
            refresh();
        },
        onError: (error: any) => {
            setEditError(errorMessage(error, 'Your comment could not be updated. Please try again.'));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteComment(id),
        onSuccess: () => {
            setDeleteTarget(null);
            refresh();
        },
        onError: (error: any) => {
            setDeleteTarget(null);
            setNotice('');
            setDraftError(errorMessage(error, 'Your comment could not be deleted. Please try again.'));
        },
    });

    if (commentsDisabled) return null;

    const submitDraft = () => {
        const text = draft.trim();
        if (!text) {
            setDraftError('Please write a comment before submitting.');
            return;
        }
        if (text.length > MAX_LENGTH) {
            setDraftError(`Comments can be up to ${MAX_LENGTH} characters.`);
            return;
        }
        if (postMutation.isPending) return;
        setDraftError('');
        setNotice('');
        postMutation.mutate(text);
    };

    const submitEdit = (id: string) => {
        const text = editText.trim();
        if (!text) {
            setEditError('Comment cannot be empty.');
            return;
        }
        if (text.length > MAX_LENGTH) {
            setEditError(`Comments can be up to ${MAX_LENGTH} characters.`);
            return;
        }
        if (editMutation.isPending) return;
        setEditError('');
        setNotice('');
        editMutation.mutate({ id, text });
    };

    const startEdit = (c: any) => {
        setEditingId(String(c._id));
        setEditText(c.body);
        setEditError('');
    };

    const loginState = { from: `${location.pathname}${location.search}` };

    return (
        <div className="mb-6">
            <div className={`${sectionHeading} mb-5`}>
                Comments {publicCount > 0 && <span className="text-grey70 dark:text-white/30">{`(${publicCount})`}</span>}
            </div>

            {isAuthenticated ? (
                <div className="mb-4">
                    <label htmlFor="event-comment-input" className="sr-only">Add a comment</label>
                    <textarea
                        id="event-comment-input"
                        className={textareaClass}
                        rows={3}
                        maxLength={MAX_LENGTH}
                        placeholder="Add a comment..."
                        value={draft}
                        onChange={e => { setDraft(e.target.value); setDraftError(''); }}
                        disabled={postMutation.isPending}
                    />
                    <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-xs text-grey40 dark:text-nightMuted">{draft.length}/{MAX_LENGTH}</span>
                        <button
                            type="button"
                            className={`${btnAccent} !h-10 !px-5`}
                            onClick={submitDraft}
                            disabled={postMutation.isPending || !draft.trim()}
                        >
                            {postMutation.isPending ? 'Submitting...' : 'Post comment'}
                        </button>
                    </div>
                    {draftError && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{draftError}</p>}
                </div>
            ) : (
                <div className={`${surface} mb-5 flex flex-col items-center p-6 text-center`}>
                    <p className="mb-4 text-[15px] text-grey40 dark:text-nightMuted">Log in to join the conversation.</p>
                    <Link to="/login" state={loginState} className={`${btnPrimary} !h-10 !px-5`}>
                        Log in
                    </Link>
                </div>
            )}

            {notice && (
                <div className="mb-4 rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800 dark:bg-green-500/10 dark:text-green-300" role="status">
                    {notice}
                </div>
            )}

            <div className="space-y-3">
                {isLoading ? (
                    <div className="py-10 text-center text-sm text-grey40 dark:text-nightMuted" role="status">Loading comments...</div>
                ) : isError ? (
                    <div className={`${surface} flex flex-col items-center gap-3 px-6 py-8 text-center`} role="alert">
                        <p className="text-sm text-grey30 dark:text-nightTextSecondary">Comments could not be loaded.</p>
                        <button type="button" className={`${btnGhost} !h-10`} onClick={() => refetch()}>Try again</button>
                    </div>
                ) : comments.length === 0 ? (
                    <div className="py-10 text-center text-grey40 dark:text-nightMuted">
                        <p className="text-sm">Be the first to comment.</p>
                    </div>
                ) : (
                    comments.map((c: any) => {
                        const id = String(c._id);
                        const own = isOwn(c);
                        const pending = own && c.status === 'pending';
                        const editing = editingId === id;
                        return (
                            <div key={id} className={`${card} !rounded-[24px] p-5`}>
                                <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
                                    <div className="flex items-start gap-3">
                                        <Avatar
                                            src={c.userInfo?.profileImage}
                                            name={c.userInfo?.firstName}
                                            seed={c.userInfo?.userId || c.userInfo?._id || c.userInfo?.id || c.userInfo?.email}
                                            size={40}
                                            className="ring-1 ring-black/[0.06] dark:ring-white/10"
                                        />
                                        <div className="flex flex-col">
                                            <div className="text-[15px] font-bold text-grey10 dark:text-white">{c.userInfo?.firstName || 'Anonymous'}</div>
                                            <div className="text-xs font-semibold text-grey40 dark:text-nightMuted">{moment(c.createdAt).fromNow()}</div>
                                        </div>
                                    </div>
                                    {pending && (
                                        <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20">
                                            Pending approval
                                        </span>
                                    )}
                                </div>

                                {editing ? (
                                    <div className="mt-3">
                                        <label htmlFor={`edit-${id}`} className="sr-only">Edit your comment</label>
                                        <textarea
                                            id={`edit-${id}`}
                                            className={textareaClass}
                                            rows={3}
                                            maxLength={MAX_LENGTH}
                                            value={editText}
                                            onChange={e => { setEditText(e.target.value); setEditError(''); }}
                                            disabled={editMutation.isPending}
                                        />
                                        <div className="mt-2 flex items-center justify-between gap-3">
                                            <span className="text-xs text-grey40 dark:text-nightMuted">{editText.length}/{MAX_LENGTH}</span>
                                            <div className="flex gap-2">
                                                <button type="button" className={smallBtn} onClick={() => setEditingId(null)} disabled={editMutation.isPending}>Cancel</button>
                                                <button
                                                    type="button"
                                                    className={`${smallBtn} !bg-primary !text-white hover:!bg-primary30 dark:!bg-nightAccent dark:!text-nightBg`}
                                                    onClick={() => submitEdit(id)}
                                                    disabled={editMutation.isPending || !editText.trim()}
                                                >
                                                    {editMutation.isPending ? 'Saving...' : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                        {c.status === 'approved' && (
                                            <p className="mt-2 text-xs text-grey40 dark:text-nightMuted">Editing sends this comment back for approval and hides it until then.</p>
                                        )}
                                        {editError && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{editError}</p>}
                                    </div>
                                ) : (
                                    <div className="mb-3 mt-2 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-grey30 dark:text-nightTextSecondary">{c.body}</div>
                                )}

                                {own && !editing && (
                                    <div className="flex gap-2">
                                        <button type="button" className={smallBtn} onClick={() => startEdit(c)}>Edit</button>
                                        <button type="button" className={smallBtn} onClick={() => setDeleteTarget(c)}>Delete</button>
                                    </div>
                                )}

                                {c.replies?.length > 0 && (
                                    <div className="ml-4 mt-3 space-y-2 sm:ml-8">
                                        {c.replies.map((r: any) => (
                                            <div key={String(r._id)} className="rounded-[20px] bg-grey99 p-4 dark:bg-white/[0.04]">
                                                <div className="mb-1 flex items-start gap-2">
                                                    <Avatar
                                                        src={r.userInfo?.profileImage}
                                                        name={r.userInfo?.firstName}
                                                        seed={r.userInfo?.userId || r.userInfo?._id || r.userInfo?.id || r.userInfo?.email}
                                                        size={40}
                                                        className="ring-1 ring-black/[0.06] dark:ring-white/10"
                                                    />
                                                    <div className="flex flex-col">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-[15px] font-bold text-grey10 dark:text-white">{r.userInfo?.firstName || 'Organizer'}</span>
                                                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary dark:bg-nightAccent/15 dark:text-nightAccent">Organizer</span>
                                                        </div>
                                                        <div className="text-xs font-semibold text-grey40 dark:text-nightMuted">{moment(r.createdAt).fromNow()}</div>
                                                    </div>
                                                </div>
                                                <div className="mt-2 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-grey30 dark:text-nightTextSecondary">{r.body}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <DeleteConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(String(deleteTarget._id))}
                title="Delete comment"
                message="Delete this comment? It will no longer be visible publicly."
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
